import { capitalize, groupBy, isNil, keys, sortBy } from 'lodash';
import LRU from 'lru';
import moment from 'moment-timezone';
import ATAPI from './AtApi';
import { config } from '../constants/config'
import { ARRIVING_THRESHOLD_IN_MS, FILTER_OUT_THRESHOLD_IN_SECONDS } from '../constants/liveDepartures';
import { ROUTE_CODE_MAPPING } from '../constants/routes';
import { recordException } from './AnalyticsService';
import CacheService from './CacheService';
import {
  fetchRoutesByStopId,
  getRouteById,
  getStopByCode,
  getStopInfo,
  getStops,
  getStopsByParentId
} from './GtfsService';
import StorageService from './StorageService';
import { normaliseGtfsDateTime } from '../transforms/GtfsDateTime';
import SortRouteShortNames from '../transforms/SortRouteShortNames';

const atApi = ATAPI.getInstance();
const { gtfsChangesCacheTTL, lastServiceHour } = config;
const KEY_FILTERED_ROUTES = 'storage_live_departure_filtered_routes';
const KEY_CACHE_FILTER_OPTION = 'cache_live_departure_filter_options';

const tripChangesCache = LRU({
  max: 500,
  maxAge: gtfsChangesCacheTTL * 1000,
});

export const isCancelled = arrivalStatus => arrivalStatus === 'cancelled';

export const getFilterRoutesOptionsByStop = async (stopCode) => {
  const key = `${KEY_CACHE_FILTER_OPTION}-${stopCode}`;
  const cachedOptions = CacheService.get(key);
  if (cachedOptions) {
    return Promise.resolve(cachedOptions);
  }

  const stop = await getStopByCode(stopCode);
  const routes = await fetchRoutesByStopId(stop?.stop_id);

  const routesGrouped = groupBy(routes, 'route_short_name');
  const routesGroupedUnique = Object.keys(routesGrouped).reduce((result, routeShortName) => {
    const routesUniqueForKey = [];
    routesGrouped[routeShortName].forEach((route) => {
      if (!routesUniqueForKey.some(item => item.route_long_name === route.route_long_name)) {
        routesUniqueForKey.push(route);
      }
    });

    return Object.assign({}, result, {
      [routeShortName]: routesUniqueForKey,
    });
  }, {});

  const routesNameListSorted = SortRouteShortNames(keys(routesGrouped));
  const options = {
    routesGrouped: routesGroupedUnique,
    routesNameListSorted,
  };
  CacheService.set(key, options);
  return options;
};

/**
 * Add essential field to movement, transferring received time string in different format
 * to a proper scheduledDepartureMomentTime, add a unique key to the movement and
 * also add the stop_code which the movement is requested for.
 * @param movement The movement to update.
 * @param stopCode The selected stop code.
 * @private
 */
const _addFieldToMovement = (movement, stopCode) => {
  const movementMapped = Object.assign({}, movement);
  // Substitute the searched stop code if the current movement doesn't have one
  if (!movementMapped.stop_code && stopCode) {
    movementMapped.stop_code = stopCode;
  }
  // operationalChanges uses `routeShortName` while the rest uses `route_short_name`
  if (movement.routeShortName) {
    movementMapped.route_short_name = movement.routeShortName;
  }

  if (movementMapped.departure_time) {
    const timeArray = movementMapped.departure_time.split(':'); // turning string like '09:30:24' into an array
    const hours = timeArray[0];
    if (hours) {
      // Some departure_times returned are like '26:30:24' so here we take the modular(24) part
      timeArray.splice(0, 1, hours % 24);
      timeArray.unshift(moment().format('DD')); // add date info
      const departureTimeStringNormalized = timeArray.join(':');

      const momentTime = moment(departureTimeStringNormalized, 'DD:HH:mm:ss');
      if (hours >= 24) {
        momentTime.add(1, 'days');
      }

      movementMapped.scheduledDepartureTime = momentTime.utc().format();
      movementMapped.scheduledDepartureMomentTime = momentTime;
    }
  } else if (movement.scheduledDepartureTime) {
    movementMapped.scheduledDepartureMomentTime = moment(movement.scheduledDepartureTime);
  }

  let key = '';
  if (movementMapped.route_short_name) {
    key += movementMapped.route_short_name;
  }

  if (movementMapped.scheduledDepartureMomentTime) {
    key += ` - ${movementMapped.scheduledDepartureMomentTime.utc().format('DD:HH:mm:ss')}`;
  }

  movementMapped.keyRouteDepartureTime = key;
  return movementMapped;
};

/**
 * Find the movement which the key matches.
 * @param movementToFind the movement whose key would be used to search
 * @param movements in which collection of movements to try to search
 * @returns the movement which key is in the collection
 * @private
 */
const _findMovement = (movementToFind, movements) => {
  if (!movements || movements.length < 1 || !movementToFind || !movementToFind.keyRouteDepartureTime) {
    return null;
  }
  return movements.find(movement => movement.keyRouteDepartureTime === movementToFind.keyRouteDepartureTime);
};

/**
 * Combine movements from multiples sources into a single collection.
 * @param movementsTwoHours the departure movements from the departures api, containing services for the next 2 hours
 * @param movementsAllDay the static movements from the stopInfo api enriched with operational changes data
 * @returns the combined movements using the unique key
 */
export const combineMovements = (movementsTwoHours, movementsAllDay) => {
  if (!movementsAllDay && !movementsTwoHours) {
    return [];
  }
  if (!movementsAllDay) {
    return movementsTwoHours;
  }
  if (!movementsTwoHours) {
    return movementsAllDay;
  }

  const movementsDepartures = movementsTwoHours.map(movement => ({
    ..._findMovement(movement, movementsAllDay),
    ...movement // data from departures should be prioritised to be used
  }));

  const fewMinutesAgo = moment().subtract(3, "minutes");
  const movementsStaticData = movementsAllDay
    .filter(movement => !_findMovement(movement, movementsTwoHours))
    .filter(movement => movement.scheduledDepartureMomentTime?.isAfter(fewMinutesAgo));

  const movementsCombined = movementsDepartures.concat(movementsStaticData);
  return sortBy(movementsCombined, ['scheduledDepartureTime', 'trip_id']);
};

/**
 * Get the departures from serviceinfo API.
 * @param stopCode Stop code
 */
export const getDepartureMovements = stopCode => atApi.getDepartures(stopCode)
  .then(({ extensions, movements }) => ({
    movements: movements
      .filter(movement => movement.departureBoardingActivity !== 'noBoarding')
      .map(movement => _addFieldToMovement(movement, stopCode)),
    messages: extensions,
  }));

/**
 * Get the operationalChanges from serviceinfo API.
 * @param stopCode Stop code
 * @param options  Contains filters (startDate and endDate) to limit the fetched data to a date/time range
 */
export const getOperationalChangesMovements = (stopCode, options) => {
  const queryParams = {};
  if (options?.startDate || options?.endDate) {
    const startDateAsMoment = options.startDate ? moment(options.startDate) : moment();
    const endDateAsMoment = options.endDate ? moment(options.endDate) : startDateAsMoment.clone().add(2, 'hours');
    queryParams.from_time = startDateAsMoment.unix();
    queryParams.to_time = endDateAsMoment.unix();
  }
  return atApi.getOperationalChanges(stopCode, queryParams)
    .then(({ movements, stopMessages }) => ({
      movements: movements?.map(movement => _addFieldToMovement(movement, stopCode)) ?? [],
      stopMessages: stopMessages ?? [],
    }));
};

const getTripChangeForRelatedStops = async (stop, date) => {
  const promises = [];
  promises.push(atApi.getChanges(stop.stop_id, date));
  // If the current stop is a station, then we are also interested to know
  // about the trip changes in any of its child stops
  if (stop.location_type === 1) {
    const childrenStops = await getStopsByParentId(stop?.stop_id);
    promises.push(
      ...childrenStops.map((childStop) => atApi.getChanges(childStop.stop_id, date))
    );
  }
  return promises;
}

/**
 * Get the GTFS trip change from serviceinfo API.
 * @param stopCode Stop code
 */
export const getTripChanges = async (stopCode) => {
  const tripChangesCacheKey = `getTripChanges_${stopCode}`;
  const cachedTripChanges = tripChangesCache.get(tripChangesCacheKey);
  if (cachedTripChanges) {
    return cachedTripChanges;
  }

  try {
    const stop = await getStopByCode(stopCode);
    const promises = [];
    if (stop) {
      const now = moment();
      promises.push(...(await getTripChangeForRelatedStops(stop, now.format('YYYYMMDD'))))

      // If the query has been done in the first few hours of the day
      // then another query is made for the previous day
      if (now.hour() <= lastServiceHour) {
        promises.push(...(await getTripChangeForRelatedStops(stop, now.subtract(1, "day").format('YYYYMMDD'))))
      }
    }

    const tripChanges = (await Promise.all(promises)).flat().filter(data => !!data);
    tripChangesCache.set(tripChangesCacheKey, tripChanges)

    return tripChanges;
  } catch (error) {
    recordException({ description: `Failed to fetch trip changes: ${error.message}` });
    return [];
  }
}

/**
 * Get the mode of the transport relates to a stop, mode is inferred using the stop's address
 * @param stop
 * @returns {string} the transport mode
 */
export const stopToRouteMode = (stop) => {
  if (!stop?.stopAddress) {
    return null;
  }

  const lowerCaseStopName = stop.stopAddress.toLowerCase();
  if (lowerCaseStopName.indexOf('train station') !== -1) {
    return 'TRAIN';
  }
  if (lowerCaseStopName.indexOf('ferry terminal') !== -1) {
    return 'FERRY';
  }

  return 'BUS';
};

/**
 * A comparator designed to sort movements using scheduledDepartureMomentTime
 * @param movement1
 * @param movement2
 * @returns {number} indicate the order
 * @private
 */
function _movementsByTimeComparator(movement1, movement2) {
  if (!movement1.scheduledDepartureMomentTime && !movement2.scheduledDepartureMomentTime) {
    return 0;
  }

  if (movement1.scheduledDepartureMomentTime && !movement2.scheduledDepartureMomentTime) {
    return -1;
  }

  if (!movement1.scheduledDepartureMomentTime && movement2.scheduledDepartureMomentTime) {
    return 1;
  }

  if (movement1.scheduledDepartureMomentTime.isBefore(movement2.scheduledDepartureMomentTime)) {
    return -1;
  }

  if (movement1.scheduledDepartureMomentTime.isAfter(movement2.scheduledDepartureMomentTime)) {
    return 1;
  }

  return 0;
}

/**
 * Get stop departure and arrival time data.
 * @param stop Stop
 * @param stopTimeUpdate Stop time update data for the service
 * @param stopList List of known stops
 * @private
 */
const _getStopTimes = (stop, stopTimeUpdate, stopList) => {
  const stopId = stop?.parent_station ?? stop?.stop_id ?? null;
  if (!stopId) {
    return {
      stopTimeDepartureData: null,
      stopTimeArrivalData: null,
    };
  }

  const stopTimeUpdateStop =
    stopTimeUpdate?.stop_id &&
    stopList.find((s) => s.stop_id === stopTimeUpdate.stop_id);
  const stopTimeUpdateStopId =
    stopTimeUpdateStop?.parent_station ?? stopTimeUpdateStop?.stop_id ?? null;
  const hasRealtime = stopTimeUpdateStopId === stopId;
  return {
    stopTimeDepartureData: hasRealtime ? stopTimeUpdate?.departure : null,
    stopTimeArrivalData: hasRealtime ? stopTimeUpdate?.arrival : null,
  };
}

/**
 * Calculate the most accurate departure moment for the service at the stop.
 * @param stopTimeDepartureData Stop time departure data for the service
 * @private
 */
const _getDepartureMoment = (stopTimeDepartureData) => {
  if (stopTimeDepartureData?.uncertainty === 0) {
    const departureMoment = moment.unix(stopTimeDepartureData.time);
    return departureMoment?.isValid() ? departureMoment : null;
  } else {
    return null;
  }
}

/**
 * Calculate the most accurate arrival moment for the service at the stop.
 * @param movement Movement data
 * @param stopTimeArrivalData Stop time arrival data for the service
 * @private
 */
const _getArrivalMoment = (movement, stopTimeUpdateArrival) => {
  const { expectedArrivalTime, expectedDepartureTime } = movement;

  let arrivalMoment;
  if (stopTimeUpdateArrival?.uncertainty === 0) {
    arrivalMoment = moment(stopTimeUpdateArrival.time)
  } else if (expectedArrivalTime) {
    arrivalMoment = moment(expectedArrivalTime);
  } else if (expectedDepartureTime) {
    arrivalMoment = moment(expectedDepartureTime);
  } else {
    return null;
  }

  return arrivalMoment?.isValid() ? arrivalMoment : null;
};

/**
 * Calculate the waiting time for the service at the stop.
 * @param {Moment | null} arrivalMoment The arrival moment of the service
 * @param {Moment | null} departureMoment The departure moment of the service
 * @private
 */
const _getWaitingTime = (arrivalMoment, departureMoment) => {
  if (arrivalMoment) {
    return arrivalMoment.valueOf() - Date.now();
  }
  if (departureMoment) {
    return departureMoment.valueOf() - Date.now();
  }
  return null;
}

/**
 * Check if the data indicate the service is departed.
 * @param {number | null} stopsAway Number of stops between the service and the stop
 * @param {Moment | undefined} departureMoment Moment of service departure from stop
 * @param {Moment | undefined} arrivalMoment Moment of service arrival to stop
 */
const _checkIfDeparted = (stopsAway, departureMoment, arrivalMoment) => {
  return (
    stopsAway < -1 ||
    ((stopsAway === null || stopsAway < 0) &&
      !!departureMoment?.isBefore(moment())) ||
    (stopsAway < 0 && !departureMoment && !arrivalMoment)
  );
}

/**
 * Check if the data indicate the service is arriving.
 * @param {number | null} stopsAway Number of stops between the service and the stop
 * @param {number | null} waitingTime Predicted waiting time in milliseconds
 */
const _checkIfArriving = (stopsAway, waitingTime) => {
  return (
    (isNil(stopsAway) || stopsAway < 1) &&
    waitingTime !== null &&
    waitingTime <= ARRIVING_THRESHOLD_IN_MS
  );
}

const _getTripDataStopId = (stopCode, stopList) => {
  const tripDataStop = stopList.find(
    (stop) => stop.stop_code === stopCode
  );
  return tripDataStop?.parent_station ?? tripDataStop?.stop_id ?? null;
}

const _getStopTimeUpdateStopId = (stopTimeUpdate, stopList) => {
  const stopTimeUpdateStop =
    stopTimeUpdate?.stop_id &&
    stopList.find((stop) => stop.stop_id === stopTimeUpdate?.stop_id);
  return stopTimeUpdateStop?.parent_station ?? stopTimeUpdateStop?.stop_id ?? null;
}

/**
 * Decide whether a service should be shown
 * @param {*} movement Movement data
 * @param {*} stopList List of known stops
 * @return {boolean} Whether movement should be filtered out
 */
const _shouldFilterOutMovement = (movement, stopList) => {
  const {
    expectedArrivalTime, expectedDepartureTime,
    scheduledDepartureMomentTime, stopsAway, stop_code, tripUpdate,
  } = movement;

  // Filtering the services which are long gone with negative stops away
  if (stopsAway < -1) {
    return true;
  }

  const tripDataStopId = _getTripDataStopId(stop_code, stopList);
  const stopTimeUpdate = tripUpdate?.trip_update?.stop_time_update;
  const stopTimeUpdateStopId = _getStopTimeUpdateStopId(stopTimeUpdate, stopList);

  const departure =
    tripDataStopId !== null && tripDataStopId === stopTimeUpdateStopId
      ? stopTimeUpdate?.departure
      : null;

  const fewMinutesAgo = moment().subtract(FILTER_OUT_THRESHOLD_IN_SECONDS, 'seconds');
  const hasLeft = departure?.uncertainty === 0 ? moment.unix(departure.time)?.isBefore(fewMinutesAgo) : false;

  // Filtering the services for which we have a known departure time of more than the threshold
  if (hasLeft) {
    return true;
  }

  // Filtering the services which have no prediction, no stop away data,
  // and the scheduled departure time of more than the threshold
  return (
    !expectedArrivalTime &&
    !expectedDepartureTime &&
    stopsAway === null &&
    !!scheduledDepartureMomentTime?.isBefore(fewMinutesAgo)
  );
};

/**
 * Compare equality of movement records using trip id and departure time.
 */
function _movementComparator(left, right) {
  return left.trip_id === right.trip_id && left.departure_timestamp === right.scheduledDepartureTime;
}

/**
 * Get the cancelled trips from realtime operational changes info and update its matching the movement.
 * @param {*} operationalChanges Realtime movement list.
 * @returns Updated movements list
 */
const _addCancellationsFromOperationalChanges = (operationalChanges) =>
  (movement) => ({
    ...movement,
    ...operationalChanges?.find(operationalChangeMovement =>
      _movementComparator(movement, operationalChangeMovement) && isCancelled(operationalChangeMovement.arrivalStatus)
    ),
  });

/**
 * Map a record from GTFS trip changes as a movement record.
 * @param {*} tripChange The GTFS trip change record.
 * @param {*} stopCode The stop code of the current stop.
 * @returns A movement record matching data from GTFS trip change.
 */
const _mapTripChangeAsMovement = async (tripChange, stopCode) => {
  const {
    route_id,
    start_date,
    stop_times,
    trip_headsign,
    trip_id,
  } = tripChange;
  const routeShortName = (await getRouteById(route_id))?.route_short_name;
  if (!routeShortName) {
    return null;
  }

  const currentStop = await getStopByCode(stopCode);
  if (!currentStop) {
    return null;
  }

  const childStops = await getStopsByParentId(currentStop.stop_id);
  const currentStopTime = stop_times.find((stop) =>
    stop.stop_id === currentStop.stop_id ||
      childStops?.map((cs) => cs.stop_id).includes(stop.stop_id)
  );
  if (!currentStopTime) {
    return null;
  }

  const {
    departure_time,
    drop_off_type,
    pickup_type,
    stop_headsign,
    stop_sequence,
  } = currentStopTime;
  const { date, time } = normaliseGtfsDateTime(start_date, departure_time) ?? {};
  const scheduledDepartureMomentTime = moment(`${date}_${time}`, 'YYYYMMDD_HH:mm:ss');
  const scheduledDepartureTime = scheduledDepartureMomentTime.utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');

  if (!scheduledDepartureMomentTime?.isValid()) {
    return null;
  }

  return {
    departure_time,
    departure_timestamp: scheduledDepartureTime,
    drop_off_type,
    end_date: 0,
    keyRouteDepartureTime: `${routeShortName} - ${scheduledDepartureMomentTime.format('DD:HH:mm:ss')}`,
    pickup_type,
    route_id,
    route_short_name: routeShortName,
    scheduledDepartureMomentTime,
    scheduledDepartureTime,
    start_date,
    stop_code: stopCode,
    stop_headsign,
    stop_sequence,
    trip_headsign,
    trip_id,
    trip_short_name: null,
  };
}

/**
 * Apply the trip changes to the list of movements.
 * @param {*} movements The current list of movements.
 * @param {*} tripChanges The GTFS trip changes.
 * @param {*} stopCode The stop code of the current stop.
 * @param {*} dateTimeRange The date time range of the current list of movements.
 * @returns Updated list of movements.
 */
const _applyTripChanges = async (movements, tripChanges, stopCode, dateTimeRange) => {
  // Categorise the trip changes into it different types
  const { removedTrips, addedTrips, changedTrips } = tripChanges.reduce((collection, tripChange) => {
    const { display, source} = tripChange;
    if (source === "Added" && display) {
      return {
        ...collection,
        addedTrips: collection.addedTrips.concat(tripChange),
      };
    } else if (!display) {
      return {
        ...collection,
        removedTrips: collection.removedTrips.concat(tripChange),
      };
    } else {
      return {
        ...collection,
        changedTrips: collection.changedTrips.concat(tripChange),
      };
    }
  }, { removedTrips: [], addedTrips: [], changedTrips: [] });

  const currentStop = await getStopByCode(stopCode);
  const childrenStops = await getStopsByParentId(currentStop?.stop_id);

  const processedMovements = movements?.reduce((arr, movement) => {
    // Remove cancelled and hidden trips
    if (removedTrips.some((trip) => trip.trip_id === movement.trip_id)) {
      return arr;
    }

    const changedTrip = changedTrips.find((trip) => trip.trip_id === movement.trip_id)
    if (changedTrip) {
      const matchedStopTime = changedTrip.stop_times.find((stopTime) => {
        return stopTime.stop_id === currentStop.stop_id || childrenStops.some((childStop) => stopTime.stop_id === childStop.stop_id);
      });

      // Apply changed head signs
      return [...arr, {
        ...movement,
        ...(matchedStopTime ? { stop_headsign: matchedStopTime?.stop_headsign } : undefined),
      }];
    } else {
      // Leave the movement as-is
      return [...arr, movement];
    }
  }, []) ?? [];

  // Add new trips, after formatting as movement
  let addedTripsFromChanges = await Promise.all(
    addedTrips.map((trip) => 
      _mapTripChangeAsMovement(trip, stopCode)
    )
  )

  // Filter the trips if a range is provided
  if (dateTimeRange) {
    const { startDate, endDate } = dateTimeRange;  
    const startRange = moment(startDate);
    const endRange = moment(endDate);
    addedTripsFromChanges = addedTripsFromChanges
      .filter((addedTrip) => 
        addedTrip?.scheduledDepartureMomentTime.isAfter(startRange) &&
        addedTrip?.scheduledDepartureMomentTime.isSameOrBefore(endRange)
      );
  }

  // Append all valid added trips, and re-sort the list
  return sortBy(
    processedMovements.concat(
      // Remove empty/invalid trips, and do not add trips that already exists in the list
      addedTripsFromChanges.filter((trips) =>
        !!trips && !processedMovements.some((pm) => pm.trip_id === trips.trip_id)
      )
    ),
    ["scheduledDepartureTime", "trip_id"]);
}

const _getRealTimeTripInfo = async (movements) => {
  let movementsToRetrieveUpdate = movements;
  // Need to limit the max number of movements to 70 due to a max query string length requirement on the tripupdates API
  const maxMovementsNumber = 70;
  if (movementsToRetrieveUpdate?.length > maxMovementsNumber) {
    const movementsSorted = movements.sort(_movementsByTimeComparator);
    movementsToRetrieveUpdate = movementsSorted.slice(0, maxMovementsNumber);
  }
  
  try {
    return (await atApi.getRealtimeTripInfo(...movementsToRetrieveUpdate.map(movement => movement.trip_id)))?.entity
  } catch (error) {
    recordException({ description: `Failed to fetch realtime trip info: ${error.message}` });
    return null;
  }
}

/**
 * Get the realtime trip update info and add them to the movements provided,
 * also will reject movements which scheduled time is after the current time.
 * @param movementsToChange The list of movements to update.
 * @param realtimeEntities The list of realtime entity records.
 * @param stopList The list of stops associated with the movements.
 * @private
 */
const _addRealtimeUpdatesMovements = (movementsToChange, realtimeEntities, stopList) => {
  let movements = movementsToChange.map(movement => Object.assign({}, movement));
  
  if (realtimeEntities?.length) {
    movements = movements.map((movementToChange) => {
      const movement = Object.assign({}, movementToChange);

      const tripUpdateEntity = realtimeEntities.find(({ trip_update }) => movement.trip_id === trip_update?.trip?.trip_id);
      if (tripUpdateEntity) {
        movement.tripUpdate = tripUpdateEntity;
      }

      const vehicleEntity = realtimeEntities.find(({ vehicle }) => movement.trip_id === vehicle?.trip?.trip_id);
      if (vehicleEntity) {
        movement.vehicleUpdate = vehicleEntity;
      }

      const tripStopSequence = tripUpdateEntity?.trip_update?.stop_time_update?.stop_sequence;
      movement.stopsAway = !!vehicleEntity &&
        typeof movement.stop_sequence === "number" &&
        typeof tripStopSequence === "number"
          ? movement.stop_sequence - tripStopSequence - 1
          : null;

      return movement;
    });
  }

  return movements.map((movementToChange) => {
    const movement = Object.assign({}, movementToChange);
    const { scheduledDepartureMomentTime, stopsAway, stop_code, tripUpdate } = movement;

    const stop = stopList.find((s) => s.stop_code === stop_code);
    
    const stopTimeUpdate = tripUpdate?.trip_update?.stop_time_update;
    const { stopTimeDeparture, stopTimeArrival } = _getStopTimes(stop, stopTimeUpdate, stopList);
    
    const arrivalMoment = _getArrivalMoment(movement, stopTimeArrival);
    const departureMoment = _getDepartureMoment(stopTimeDeparture);
    const waitingTime = _getWaitingTime(arrivalMoment, departureMoment);

    const arriving = _checkIfArriving(stopsAway, waitingTime);
    const departed = _checkIfDeparted(stopsAway, departureMoment, arrivalMoment);
    const isScheduled = scheduledDepartureMomentTime && (!waitingTime || !departed || !arriving);
    
    return {
      ...movement,
      departed,
      departureMoment,
      arriving,
      arrivalMoment,
      waitingTime,
      isScheduled,
    };
  });
}

/**
 * Update the static movement data by filtering out invalid records, and adding more data.
 * @param {*} staticMovements The list of static movements.
 * @param {*} operationalChanges Realtime updates from serviceinfo API.
 * @param {*} tripChanges GTFS trip changes.
 * @param {*} stopCode The stop code.
 * @param {*} options Contains filters (startDate and/or endDate) to limit the fetched data to a date/time range.
 */
export const processStopInfoMovements = async (staticMovements, operationalChanges, tripChanges, stopCode, options) => {
  // Determine the date/time range of the list by using options
  const { startDate, endDate } = options ?? {};
  const dateTimeRange = {
    startDate: startDate ?? moment().utc().format(),
    endDate: endDate ?? moment(startDate).add(2, "hours").utc().format()
  };

  const staticMovementsWithChanges = await _applyTripChanges(
    staticMovements, tripChanges.flat(), stopCode, dateTimeRange
  );

  if (!staticMovementsWithChanges?.length) {
    return [];
  }
  
  const processedMovements = staticMovementsWithChanges
    .filter(movement => movement.pickup_type !== 1)
    .map(movement => _addFieldToMovement(movement, stopCode));
  if (operationalChanges?.length) {
    return processedMovements.map(_addCancellationsFromOperationalChanges(operationalChanges));
  } else {
    return processedMovements;
  }
};

/**
 * Get the realtime movements from the stopInfo and realtime Api, essential info is the stops away and departed statuses.
 * @param staticMovements The list of static movements
 * @param stopCode Stop code
 * @param options Contains filters (startDate and/or endDate) to limit the fetched data to a date/time range
 */
export const getMovements = async (staticMovements, stopCode, options) => {
  const [{ movements, messages }, operationalChanges, tripChanges, staticStopsInfo] = await Promise.all([
    getDepartureMovements(stopCode),
    getOperationalChangesMovements(stopCode, options),
    getTripChanges(stopCode),
    staticMovements ? Promise.resolve([...staticMovements]) : getStopInfo(stopCode, options)
  ]);

  const processedStopInfoMovements = await processStopInfoMovements(
    staticStopsInfo,
    operationalChanges?.movements,
    tripChanges,
    stopCode,
    options);
  const combinedMovements = combineMovements(movements ?? [], processedStopInfoMovements);
  const realTimeTripInfo = await _getRealTimeTripInfo(combinedMovements);

  const stopList = await getStopList(combinedMovements, realTimeTripInfo);

  const enrichedCombinedMovements = _addRealtimeUpdatesMovements(combinedMovements, realTimeTripInfo, stopList);

  return {
    movements: enrichedCombinedMovements
      .filter(movementToFilter => !_shouldFilterOutMovement(movementToFilter, stopList)),
    staticStopsInfo,
    messages,
  };
};

/**
 * Get the more movements from the stopInfo and realtime API.
 * @param stopCode Stop code
 * @param options  Contains filters (startDate and/or endDate) to limit the fetched data to a date/time range
 */
export const getMoreMovements = async (stopCode, options) => {
  const [operationalChanges, tripChanges, stopInfo] = await Promise.all([
    getOperationalChangesMovements(stopCode, options),
    getTripChanges(stopCode),
    getStopInfo(stopCode, options),
  ]);
  const processedStopInfoMovements = await processStopInfoMovements(
    stopInfo,
    operationalChanges?.movements,
    tripChanges,
    stopCode,
    options);
  return {
    movements: sortBy(processedStopInfoMovements, ['scheduledDepartureTime', 'trip_id']),
    staticStopsInfo: stopInfo,
  };
};

/**
 * Map the route code and mode to a proper name to be displayed.
 * @param routeCode The route short name
 * @param routeMode The transport mode of the route
 * @returns {*|string}
 */
export const routeCodeToName = (routeCode, routeMode) =>
  ROUTE_CODE_MAPPING[routeCode] || `${capitalize(routeMode)} ${routeCode || ''}`;

export const getFilteredRoutes = (stopCode) => StorageService.getItem(KEY_FILTERED_ROUTES)
  .then(JSON.parse)
  .then(routesFiltered => routesFiltered?.[`${stopCode}`]);

export const setFilteredRoutes = (routeShortNames, stopCode) => StorageService.getItem(KEY_FILTERED_ROUTES)
  .then(JSON.parse)
  .then((routesFiltered) => {
    const routesFilteredNew = Object.assign({}, routesFiltered);
    routesFilteredNew[stopCode] = routeShortNames;
    return StorageService.setItem(KEY_FILTERED_ROUTES, JSON.stringify(routesFilteredNew));
  });

/**
 * Building a list of GTFS stops used by the stopInfo and realtime data.
 * @param stopInfo The list of movements
 * @param realtimeEntities The list of realtime entity records
 * @returns List of stops
 */
export const getStopList = async (stopInfo, realtimeEntities) => {
  const entitiesStopIds =
    realtimeEntities?.reduce((accStopIds, { trip_update }) => {
      const tripStopId = trip_update?.stop_time_update?.stop_id;
      if (!tripStopId) {
        return accStopIds;
      }
      return accStopIds.includes(tripStopId)
        ? accStopIds
        : accStopIds.concat(tripStopId);
    }, []) ?? [];

  const stopInfoStopCodes =
  stopInfo?.reduce((accStopIds, { stop_code }) => {
    if (!stop_code) {
      return accStopIds;
    }
    return accStopIds.includes(stop_code)
      ? accStopIds
      : accStopIds.concat(stop_code);
  }, []) ?? [];

  const allStops = await getStops();
  return allStops.filter(({ stop_code, stop_id }) => (
    entitiesStopIds.includes(stop_id) || stopInfoStopCodes.includes(stop_code)
  ));
};
